import { useState, useRef, useEffect } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  Home,
  Target,
  Trophy,
  BookOpen,
  LogOut,
  Flame,
  FileText,
  Gamepad2,
  ScanLine,
  Menu,
  X,
  Settings,
  ChevronDown,
} from 'lucide-react'

export default function Layout() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const isParent = user?.user_type === 'parent'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [userMenuOpen])

  const studentLinks = [
    { to: '/student', icon: Home, label: 'Dashboard' },
    { to: '/student/arcade', icon: Gamepad2, label: 'Game Arcade' },
    { to: '/student/scans', icon: FileText, label: 'My Scans' },
    { to: '/student/challenges', icon: Target, label: 'Challenges' },
    { to: '/student/achievements', icon: Trophy, label: 'Achievements' },
    { to: '/student/skills', icon: BookOpen, label: 'Skills' },
  ]

  const parentLinks = [
    { to: '/parent', icon: Home, label: 'Home' },
    { to: '/parent/scans', icon: ScanLine, label: 'Scans' },
    { to: '/parent/courses', icon: BookOpen, label: 'Courses' },
    { to: '/parent/games', icon: Gamepad2, label: 'Arcade' },
  ]

  const links = isParent ? parentLinks : studentLinks

  const closeMobileMenu = () => setMobileMenuOpen(false)

  const NavContent = () => (
    <>
      {/* Logo */}
      <div className="h-14 flex items-center px-4 border-b border-gray-800/50">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-quest-primary to-blue-400 flex items-center justify-center mr-2 shadow-lg shadow-quest-primary/20">
          <BookOpen className="w-3.5 h-3.5 text-white" aria-hidden="true" />
        </div>
        <span className="text-lg font-bold tracking-tight text-white">Canvas Quest</span>
      </div>

      {/* User info */}
      {!isParent && (
        <div className="px-4 py-4 border-b border-gray-800/50">
          <div className="flex items-center gap-3">
            <div className="level-badge" aria-label={`Level ${user?.level || 1}`}>
              {user?.level || 1}
            </div>
            <div>
              <p className="font-medium text-white">{user?.name}</p>
              <div className="flex items-center gap-1 text-sm text-gray-400">
                <Flame className="w-4 h-4 text-orange-500" aria-hidden="true" />
                <span>0 day streak</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-3 py-3" aria-label="Main navigation">
        <ul className="space-y-0.5" role="list">
          {links.map(({ to, icon: Icon, label }) => {
            const isActive = to === '/parent' || to === '/student'
              ? location.pathname === to
              : location.pathname.startsWith(to)
            return (
              <li key={to}>
                <Link
                  to={to}
                  onClick={closeMobileMenu}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm font-medium ${
                    isActive
                      ? 'bg-quest-primary/10 text-quest-primary'
                      : 'text-gray-400 hover:bg-quest-primary/5 hover:text-quest-primary'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon className="w-4 h-4" aria-hidden="true" />
                  <span>{label}</span>
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>

      {/* Footer - logout for student view (parent has it in top bar) */}
      {!isParent && (
        <div className="p-4 border-t border-gray-800/50">
          <button
            onClick={() => {
              closeMobileMenu()
              logout()
            }}
            className="flex items-center gap-3 px-4 py-2 w-full rounded-xl
                       text-gray-400 hover:bg-quest-primary/5 hover:text-quest-primary transition-all text-sm"
          >
            <LogOut className="w-4 h-4" aria-hidden="true" />
            <span>Logout</span>
          </button>
        </div>
      )}
    </>
  )

  return (
    <div className="min-h-screen flex">
      {/* Skip link for accessibility */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Mobile menu button */}
      <button
        className="mobile-menu-btn"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={mobileMenuOpen}
      >
        {mobileMenuOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <Menu className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <div
          className="mobile-sidebar-overlay"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`mobile-sidebar ${mobileMenuOpen ? 'open' : 'closed'}`}
        aria-label="Mobile navigation"
      >
        <NavContent />
      </aside>

      {/* Desktop sidebar */}
      <aside className="desktop-sidebar w-52 bg-surface-sidebar border-r border-gray-800 flex-col hidden lg:flex">
        <NavContent />
      </aside>

      {/* Main content */}
      <main
        id="main-content"
        className="flex-1 overflow-auto bg-surface-base"
        tabIndex={-1}
      >
        {/* Top bar with user dropdown */}
        {isParent && (
          <div className="flex items-center justify-end px-4 lg:px-6 py-2 border-b border-gray-800/30">
            <div ref={userMenuRef} className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-surface-hover transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-gradient-to-r from-quest-primary to-quest-secondary flex items-center justify-center text-white text-xs font-bold">
                  {user?.name?.charAt(0) || 'P'}
                </div>
                <span className="text-sm text-gray-300">{user?.name}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-1 w-44 bg-surface-card border border-surface-border rounded-lg shadow-xl py-1 z-50">
                  <button
                    onClick={() => { setUserMenuOpen(false); navigate('/parent/settings') }}
                    className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-300 hover:bg-surface-hover hover:text-white transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                  <div className="border-t border-surface-border my-1" />
                  <button
                    onClick={() => { setUserMenuOpen(false); logout() }}
                    className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-300 hover:bg-surface-hover hover:text-red-400 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="p-4 lg:p-6 pt-14 lg:pt-4">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
